/* eslint-disable react/no-array-index-key */
import { PlusIcon } from "@heroicons/react/20/solid";
import { useMemo, useCallback, memo, useState, useEffect } from "react";
import { Select } from "@mantine/core";
import { useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import FilterCondition from "../../../../../shared/filter/FilterCondition";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "../../../../../shared/filter/filterStyles";
import { buildEntityOptions } from "../../../../../shared/filter/utils/entityDisplayUtils";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";

type Props = {
  selectedFilters?: any[];

  handleFilter?: (
    field: any,
    operator: any,
    value: any,
    type: any,
    entityType: any,
    id: number,
    filterId: number,
  ) => void;
  handleRemove?: (currentKey: number, filterId?: number) => void;
  handleAdd?: (filterId?: number) => void;
  filterId?: number;
  entityRelation?: string;
  className?: string;
};

const SignalFilterContainer = memo(({
  selectedFilters = [],
  handleFilter,
  handleRemove,
  handleAdd,
  filterId,
  entityRelation,
  className,
}: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const [selectedEntity, setSelectedEntity] = useState<string>(entityRelation || "accounts");

  useEffect(() => {
    if (entityRelation) {
      setSelectedEntity(entityRelation);
    }
  }, [entityRelation]);

  const handleEntityChange = useCallback((val: string) => {
    setSelectedEntity(val);

    // Reset filters when entity changes
    if (selectedFilters && selectedFilters.length > 0 && handleFilter && handleRemove && filterId !== undefined) {
      // 1. Reset the first filter
      handleFilter(
        "", // field
        "", // operator
        "", // value
        "string", // type
        "column", // entityType
        0, // id (predicate index)
        filterId // filterId (group index)
      );

      // 2. Remove any additional filters (iterate backwards to avoid index shifting issues)
      for (let i = selectedFilters.length - 1; i > 0; i -= 1) {
        handleRemove(i, filterId);
      }
    }
  }, [selectedFilters, handleFilter, handleRemove, filterId]);

  const entityOptions = useMemo(
    () => buildEntityOptions(workspaceSchema),
    [workspaceSchema],
  );

  // Memoize callbacks to prevent unnecessary re-renders
  const handleAddClick = useCallback(() => {
    if (handleAdd && filterId !== undefined) {
      handleAdd(filterId);
    }
  }, [handleAdd, filterId]);
  // Memoize filter list to prevent unnecessary re-renders
  const filtersList = useMemo(() => {
    if (!selectedFilters || selectedFilters.length === 0) {
      return null;
    }

    // Provide default no-op functions if handlers are undefined
    const safeHandleRemove = handleRemove || (() => { });
    const safeHandleFilter = handleFilter || (() => { });

    return selectedFilters.map((filter: any, index: number) => {
      // Create stable key based on filter content and position
      const filterKey = filter?.field
        ? `predicate-${filterId}-${index}-${filter.field}`
        : `predicate-${filterId}-${index}-empty`;

      return (
        <FilterCondition
          key={filterKey}
          filter={filter}
          entityRelation={selectedEntity}
          onRemove={safeHandleRemove}
          workspaceSchema={workspaceSchema}
          currentKey={index}
          filterId={filterId!}
          onCompleteFilter={safeHandleFilter}
        />
      );
    });
  }, [selectedFilters, filterId, selectedEntity, handleRemove, handleFilter, workspaceSchema]);

  return (
    <div className={`w-full ${className || ""}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Match</span>
        <Select
          size="xs"
          classNames={FILTER_SELECT_CLASSES}
          comboboxProps={FILTER_COMBOBOX_PROPS}
          value={selectedEntity}
          onChange={(val) => handleEntityChange(val as string)}
          data={entityOptions}
        />
      </div>

      <div className="flex flex-col gap-1">
        {filtersList}

        <div className="w-full flex justify-start pt-0.5">
          <button
            type="button"
            onClick={handleAddClick}
            className="inline-flex items-center rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <PlusIcon className="mr-1 flex-shrink-0 h-3 w-3" />
            Add condition
          </button>
        </div>
      </div>
    </div>
  );
});

SignalFilterContainer.displayName = "SignalFilterContainer";

export default SignalFilterContainer;
